import { Box, Container, Typography, Button } from '@mui/material';
import React, { useState, useEffect } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import NumberFormat from 'react-number-format';
import styled from 'styled-components';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import IconButton from '@mui/material/IconButton';

import { useAppSelector, useAppDispatch } from '../../store';
import { addPrice } from '../../store/mobileSlice';

import useDebounceValue from '../../hooks/useDebounceValue';
import { currencyFormat, removeCurrencyFormat, currencyFormatCN } from '../../utils/index';

const WapperInputNumber = styled.div`
  position: relative;
  border: 1px solid #f3f3f3;
  border-radius: 4px;
  width: 100%;
  height: 40px;
  background-color: #f3f3f3;

  & label {
    position: absolute;
    top: 7px;
    left: 4px;
    padding: 0 8px 0 8px;
    transition: 0.1s;
  }

  & input:focus ~ label,
  input:not([value='']) ~ label {
    font-size: 12px;
    transform: translateY(-20px);
    background-color: white;
  }

  & input {
    border: none;
    width: 100%;
    height: 100%;
    outline: none;
    padding: 0 8px 0 8px;
  }
`;

const initialPriceMinimun = [
  0, 100000, 300000, 500000, 1000000, 1500000, 2000000, 3000000, 4000000, 5000000, 6000000, 7000000, 8000000, 10000000,
  15000000, 20000000, 30000000, 40000000,
];
const initialPriceMinimunRent = [0, 3000, 5000, 10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000];

const rentAction = ['เช่า', 'rent', '租'];

const pagePriceName = {
  th: {
    title: 'ราคา',
    reset: 'รีเซ็ต',
    submit: 'ยืนยัน',
    min: 'ขั้นต่ำ',
    max: 'สูงสุด',
    currency1: 'บาท',
    currency2: 'บาท',
  },
  en: {
    title: 'Price',
    reset: 'reset',
    submit: 'Confirm',
    min: 'min',
    max: 'max',
    currency1: 'THB',
    currency2: 'THB',
  },
  cn: {
    title: '价格',
    reset: '重设',
    submit: '确定',
    min: '最低',
    max: '最高',
    currency1: '万泰铢',
    currency2: '泰铢',
  },
};

interface SelectPriceProps {
  listType: 'action' | 'category' | 'locations' | 'price';
  open: boolean;
  handlePopupSelected: (key: string, value: boolean) => void;
  language: 'th' | 'en' | 'cn';
}

interface FocusPriceInputType {
  priceMin: boolean;
  priceMax: boolean;
}

const SelectPrice: React.FC<SelectPriceProps> = ({ listType, open, handlePopupSelected, language }) => {
  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.mobile.actions);
  const [priceMax, setPriceMax] = useState<string>('');
  const [priceMin, setPriceMin] = useState<string>('');
  const [isPriceMin, setIsPriceMin] = useState<boolean>(false);
  const [priceMaxList, setPriceMaxList] = useState<number[]>([]);
  const [priceMinList, setPriceMinList] = useState<number[]>(initialPriceMinimun);
  const [showPriceInput, setShowPriceInput] = useState<FocusPriceInputType>({
    priceMin: false,
    priceMax: false,
  });

  const finalPriceMin = useDebounceValue(priceMin);
  const finalPriceMax = useDebounceValue(priceMax);

  useEffect(() => {
    if (rentAction.includes(action)) {
      setPriceMinList(initialPriceMinimunRent);
    }
  }, [finalPriceMin, action]);

  useEffect(() => {
    if (finalPriceMax) {
      setPriceMaxList(calculateMaximumPriceList(removeCurrencyFormat(finalPriceMax)));
    }
  }, []);

  useEffect(() => {
    if (finalPriceMin === '') {
      setIsPriceMin(false);
    } else {
      setIsPriceMin(true);
      setPriceMaxList(calculateMaximumPriceList(removeCurrencyFormat(finalPriceMin)));
      setPriceMinList(calculateMaximumPriceList(removeCurrencyFormat(finalPriceMin)));
    }
  }, [finalPriceMin, isPriceMin]);

  const calculateMaximumPriceList = (price: number): number[] | [] => {
    let nextPrice;
    if (rentAction.includes(action)) {
      nextPrice = price === 0 ? 2000 : price;
    } else {
      nextPrice = price === 0 ? 100000 : price;
    }
    if (!isPriceMin || price.toString().length > 14) return [];
    const PERCENT = 1.1;

    const result: number[] = [];
    for (let index = 0; index < 15; index++) {
      let tempPrice = 0;
      switch (nextPrice.toString().length) {
        case 4:
          tempPrice = Math.round(
            Number(
              `${Math.floor(nextPrice * PERCENT)
                .toString()
                .slice(0, -2)}00`,
            ),
          );
          if (tempPrice.toString().length === 5) {
            tempPrice = Math.round(
              Number(
                `${Math.floor(nextPrice * PERCENT)
                  .toString()
                  .slice(0, -3)}000`,
              ),
            );
          }
          break;
        case 5:
          tempPrice = Math.round(
            Number(
              `${Math.floor(nextPrice * PERCENT)
                .toString()
                .slice(0, -3)}000`,
            ),
          );
          if (tempPrice.toString().length === 6) {
            tempPrice = Math.round(
              Number(
                `${Math.floor(nextPrice * PERCENT)
                  .toString()
                  .slice(0, -4)}0000`,
              ),
            );
          }
          break;
        case 6:
          tempPrice = Math.round(
            Number(
              `${Math.floor(nextPrice * PERCENT)
                .toString()
                .slice(0, -4)}0000`,
            ),
          );
          if (tempPrice.toString().length === 7) {
            tempPrice = Math.round(
              Number(
                `${Math.floor(nextPrice * PERCENT)
                  .toString()
                  .slice(0, -5)}00000`,
              ),
            );
          }
          break;
        case 7:
          tempPrice = Math.round(
            Number(
              `${Math.floor(nextPrice * PERCENT)
                .toString()
                .slice(0, -5)}00000`,
            ),
          );
          if (tempPrice.toString().length === 8) {
            tempPrice = Math.round(
              Number(
                `${Math.floor(nextPrice * PERCENT)
                  .toString()
                  .slice(0, -6)}000000`,
              ),
            );
          }
          break;
        case 8:
          tempPrice = Math.round(
            Number(
              `${Math.floor(nextPrice * PERCENT)
                .toString()
                .slice(0, -6)}000000`,
            ),
          );
          if (tempPrice.toString().length === 9) {
            tempPrice = Math.round(
              Number(
                `${Math.floor(nextPrice * PERCENT)
                  .toString()
                  .slice(0, -7)}0000000`,
              ),
            );
          }
          break;
        case 9:
          tempPrice = Math.round(
            Number(
              `${Math.floor(nextPrice * PERCENT)
                .toString()
                .slice(0, -7)}0000000`,
            ),
          );
          if (tempPrice.toString().length === 10) {
            tempPrice = Math.round(
              Number(
                `${Math.floor(nextPrice * PERCENT)
                  .toString()
                  .slice(0, -8)}00000000`,
              ),
            );
          }
          break;
        case 10:
          tempPrice = Math.round(
            Number(
              `${Math.floor(nextPrice * PERCENT)
                .toString()
                .slice(0, -8)}00000000`,
            ),
          );
          if (tempPrice.toString().length === 11) {
            tempPrice = Math.round(
              Number(
                `${Math.floor(nextPrice * PERCENT)
                  .toString()
                  .slice(0, -9)}000000000`,
              ),
            );
          }
          break;
        default:
          break;
      }
      result.push(tempPrice);
      nextPrice = tempPrice;
    }

    return result;
  };

  const handleMinimunSelected = (price: number) => {
    setPriceMin(price.toLocaleString());
    setPriceMaxList(calculateMaximumPriceList(price));
    setShowPriceInput((prevState) => ({ ...prevState, ['priceMin']: false }));
  };

  const handleMaximunSelected = (price: number) => {
    setPriceMax(price.toLocaleString());
    setShowPriceInput((prevState) => ({ ...prevState, ['priceMax']: false }));
  };

  const onSubmit = () => {
    dispatch(
      addPrice({
        priceMin: removeCurrencyFormat(finalPriceMin),
        priceMax: removeCurrencyFormat(finalPriceMax),
      }),
    );
    handlePopupSelected(listType, false);
  };

  return (
    <Box
      className={`${
        open ? 'opacity-100 z-20' : 'opacity-0 -z-10'
      } absolute w-screen h-screen bg-[#f3f3f3] top-0 left-0 py-4`}>
      <Container
        sx={{
          height: '100%',
        }}>
        <IconButton
          className={`${!open && '!hidden'}`}
          sx={{
            position: 'absolute',
          }}
          onClick={() => {
            setPriceMax('');
            setPriceMin('');
            setPriceMaxList([]);
            setPriceMinList(rentAction.includes(action) ? initialPriceMinimunRent : initialPriceMinimun);
            handlePopupSelected(listType, false);
          }}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <Typography fontSize={20} lineHeight={2} sx={{ mb: 2, textAlign: 'center' }}>
          {pagePriceName[language].title}
        </Typography>
        <Box className="flex gap-x-4">
          <WapperInputNumber>
            <NumberFormat
              id="priceMin"
              name="priceMin"
              thousandSeparator={true}
              value={priceMin}
              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                setPriceMin(event.currentTarget.value);
                setShowPriceInput((prevState) => ({ ...prevState, ['priceMin']: false }));
                setPriceMaxList(calculateMaximumPriceList(removeCurrencyFormat(event.currentTarget.value)));
              }}
              onFocus={() => {
                const results = {
                  priceMax: false,
                  priceMin: true,
                };
                setShowPriceInput(results);
              }}
            />
            <label htmlFor="priceMin" className="text-gray-400" style={{}}>
              {pagePriceName[language].min}
            </label>
          </WapperInputNumber>
          <WapperInputNumber>
            <NumberFormat
              id="priceMax"
              name="priceMax"
              thousandSeparator={true}
              value={priceMax}
              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                setPriceMax(event.currentTarget.value);
                setShowPriceInput((prevState) => ({ ...prevState, ['priceMax']: false }));
                if (!event.currentTarget.value) {
                  setPriceMaxList(calculateMaximumPriceList(removeCurrencyFormat(finalPriceMin)));
                }
              }}
              onFocus={() => {
                const results = {
                  priceMax: true,
                  priceMin: false,
                };
                setShowPriceInput(results);
                setPriceMaxList(calculateMaximumPriceList(removeCurrencyFormat(finalPriceMin)));
              }}
            />
            <label htmlFor="priceMax" className="text-gray-400">
              {pagePriceName[language].max}
            </label>
          </WapperInputNumber>
        </Box>
        <Box className="flex bg-white mt-2">
          <Box className="w-full overflow-y-auto max-h-[calc(100vh_-_32px_-_56px_-_40px_-_100px)]">
            <List className="w-full" disablePadding>
              {(!finalPriceMin || showPriceInput.priceMin) &&
                priceMinList.map((price, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton onClick={() => handleMinimunSelected(price)}>
                      <ListItemText className="text-right">{`${currencyFormatCN(
                        currencyFormat(price),
                        action,
                        language,
                      )} ${
                        !rentAction.includes(action) && language === 'cn'
                          ? pagePriceName[language].currency1
                          : pagePriceName[language].currency2
                      }`}</ListItemText>
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Box>
          <Box
            sx={{
              width: '100%',
            }}>
            <Box className="w-full overflow-y-auto max-h-[calc(100vh_-_32px_-_56px_-_40px_-_100px)]">
              <List className="w-full" disablePadding>
                {((finalPriceMin && !finalPriceMax) || showPriceInput.priceMax) &&
                  priceMaxList.map((price, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemButton onClick={() => handleMaximunSelected(price)}>
                        <ListItemText className="text-right">{`${currencyFormatCN(
                          currencyFormat(price),
                          action,
                          language,
                        )} ${
                          !rentAction.includes(action) && language === 'cn'
                            ? pagePriceName[language].currency1
                            : pagePriceName[language].currency2
                        }`}</ListItemText>
                      </ListItemButton>
                    </ListItem>
                  ))}
              </List>
            </Box>
          </Box>
        </Box>
        <Box
          className={`flex justify-between ${!showPriceInput.priceMax && !showPriceInput.priceMin ? 'mt-32' : 'mt-8'}`}>
          {removeCurrencyFormat(finalPriceMax) > removeCurrencyFormat(finalPriceMin) && (
            <>
              <Button
                sx={{
                  marginRight: 4,
                }}
                variant="outlined"
                color="error"
                onClick={() => {
                  setPriceMax('');
                  setPriceMin('');
                  setPriceMinList(rentAction.includes(action) ? initialPriceMinimunRent : initialPriceMinimun);
                  dispatch(
                    addPrice({
                      priceMin: 0,
                      priceMax: 0,
                    }),
                  );
                }}>
                {pagePriceName[language].reset}
              </Button>
              <Button className="!bg-[#f4762a]" variant="contained" onClick={() => onSubmit()}>
                {pagePriceName[language].submit}
              </Button>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default SelectPrice;
